// ============================================
// FILE: src/App.js
// ============================================
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Pages
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import Login from './pages/Login';
import SupplierRegister from './pages/SupplierRegister';
import SupplierLogin from './pages/SupplierLogin';
import SupplierDashboard from './pages/supplier/Dashboard';
import SupplierProducts from './pages/supplier/Products';
import SupplierOrders from './pages/supplier/Orders';
import UserProducts from './pages/user/Products';
import UserCart from './pages/user/Cart';
import UserOrders from './pages/user/Orders';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/admin/Dashboard';
import AdminSuppliers from './pages/admin/Suppliers';
import AdminOrders from './pages/admin/Orders';
import AdminUsers from './pages/admin/Users';
import AdminSliders from './pages/admin/Sliders';
import AdminAdvertisements from './pages/admin/Advertisements';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import useAuthStore from './store/authStore';

const queryClient = new QueryClient();

// Firebase config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

function App() {
  const { token, userType } = useAuthStore();

  useEffect(() => {
    if (token && 'Notification' in window) {
      requestNotificationPermission();
    }
  }, [token]);

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const messaging = getMessaging(app);
        const fcmToken = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });
        console.log('FCM Token:', fcmToken);

        // Listen for foreground messages
        onMessage(messaging, (payload) => {
          console.log('Message received:', payload);
          new Notification(payload.notification.title, {
            body: payload.notification.body
          });
        });
      }
    } catch (error) {
      console.error('Notification permission error:', error);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="min-h-screen">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={userType === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/supplier/register" element={<SupplierRegister />} />
            <Route path="/supplier/login" element={<SupplierLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* User Routes */}
            <Route path="/products" element={userType === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <UserProducts />} />
            <Route path="/cart" element={<ProtectedRoute userType="user"><UserCart /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute userType="user"><UserOrders /></ProtectedRoute>} />

            {/* Supplier Routes */}
            <Route path="/supplier/dashboard" element={<ProtectedRoute userType="supplier"><SupplierDashboard /></ProtectedRoute>} />
            <Route path="/supplier/products" element={<ProtectedRoute userType="supplier"><SupplierProducts /></ProtectedRoute>} />
            <Route path="/supplier/orders" element={<ProtectedRoute userType="supplier"><SupplierOrders /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin/dashboard" element={<ProtectedRoute userType="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/suppliers" element={<ProtectedRoute userType="admin"><AdminSuppliers /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute userType="admin"><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute userType="admin"><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/sliders" element={<ProtectedRoute userType="admin"><AdminSliders /></ProtectedRoute>} />
            <Route path="/admin/advertisements" element={<ProtectedRoute userType="admin"><AdminAdvertisements /></ProtectedRoute>} />

            <Route path="/about" element={<AboutUs />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        <Toaster position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;